import {Component, OnInit} from '@angular/core';
import {
  V3Address,
  V3ContactEntry,
  V3Person,
  V3PersonalSensitiveDetails,
  V3PersonDetails,
  V3Settings
} from "../../model/v3/people";
import {V3FileItem} from "../../model/v3/file";
import {ActivatedRoute} from "@angular/router";
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";
import {AuthorizationServiceProvider} from "../../services/authorization-service.provider";
import {concat, finalize, last, Observable, of, switchMap, tap} from "rxjs";
import {CasaNativeApiV3} from "../../services/api/casa-native-api";
import {catchError} from "rxjs/operators";
import {PasswordChangeRequest} from "../../model/casa/casa-model";

@Component({
  selector: 'app-profile-page',
  templateUrl: './profile-page.component.html',
  styleUrl: './profile-page.component.scss',
  standalone: false
})
export class ProfilePageComponent implements OnInit {
  busy = false
  personId: number
  saved = false
  person: V3Person
  personDetails: V3PersonDetails
  sensitiveDetails: V3PersonalSensitiveDetails
  settings: V3Settings
  address: V3Address
  photo: V3FileItem
  contacts: V3ContactEntry[];
  role: "student" | "teacher"
  private locale: string
  profileOption?: string | null

  savingObservables: Observable<any>[] = []

  constructor(
    route: ActivatedRoute,
    private apiProvider: NativeServiceApiProvider,
    private provider: AuthorizationServiceProvider) {
    this.role = route.snapshot.data["role"]?.toLocaleLowerCase()
    route.paramMap.subscribe(params => {
      this.profileOption = params.get("profileOption");
    })
  }

  ngOnInit(): void {
    this.provider.getAuthDetailsService().pipe(
      switchMap(api => api.getSelfPersonId()),
      tap(personId => this.personId = personId),
      switchMap(_ => this.apiProvider.casa()),
      switchMap(api => this.loadPersonData(api))
    ).subscribe()
  }

  loadPersonData(api: CasaNativeApiV3) {
    this.busy = true
    return this.loadPerson(api).pipe(
      switchMap(_ => this.loadPhoto(api)),
      switchMap(_ => this.loadSettings(api)),
      switchMap(_ => this.loadSensitive(api)),
      switchMap(_ => this.loadAddress(api)),
      switchMap(_ => this.loadContacts(api)),
      finalize(() => this.busy = false),
    )
  }

  loadPerson(api: CasaNativeApiV3) {
    return api.getPerson(this.personId).pipe(
      tap(person => {
        this.personId = person.id
        this.person = person
        this.personDetails = person.details;
      })
    )
  }

  loadPhoto(api: CasaNativeApiV3) {
    return api.getPhoto(this.personId).pipe(
      tap(photo => this.photo = photo),
      catchError(_ => of(null))
    )
  }

  loadSettings(api: CasaNativeApiV3) {
    return api.getSettings(this.personId).pipe(
      tap(settings => {
        this.settings = settings
        this.locale = settings.locale
      })
    )
  }

  loadSensitive(api: CasaNativeApiV3) {
    return api.getSensitive(this.personId).pipe(
      tap(sensitiveDetails => this.sensitiveDetails = sensitiveDetails)
    )
  }

  loadAddress(api: CasaNativeApiV3) {
    return api.getAddress(this.personId).pipe(
      tap(address => this.address = address)
    )
  }

  loadContacts(api: CasaNativeApiV3) {
    return api.listContacts(this.personId).pipe(
      tap(contactEntries => this.contacts = contactEntries)
    )
  }

  changePassword(request: PasswordChangeRequest) {
    this.busy = true
    this.provider.getCommonRoutines().pipe(
      switchMap(api => api.changePassword(request)),
      finalize(() => {
        this.saved = true
        this.busy = false
        setTimeout(() => {
          this.saved = false
        }, 2000);
      })
    ).subscribe()
  }

  savePersonDetails(value: V3PersonDetails) {
    this.savingObservables.push(
      this.apiProvider.casa().pipe(
        switchMap(api => api.updatePerson(this.personId, value).pipe(
          switchMap(_ => this.loadPerson(api)),
        )),
      )
    )
  }

  saveSensitiveDetails(value: V3PersonalSensitiveDetails) {
    this.savingObservables.push(
      this.apiProvider.casa().pipe(
        switchMap(api => api.updateSensitive(this.personId, value).pipe(
          switchMap(_ => this.loadSensitive(api)),
        )),
      )
    )
  }

  saveSettings(value: V3Settings) {
    this.savingObservables.push(
      this.apiProvider.casa().pipe(
        switchMap(api => api.updateSettings(this.personId, value).pipe(
          switchMap(_ => api.getSettings(this.personId)),
          tap(settings => this.settings = settings)
        )),
      )
    )
  }

  saveAddress(value: V3Address) {
    this.savingObservables.push(
      this.apiProvider.casa().pipe(
        switchMap(api => api.updateAddress(this.personId, value).pipe(
          switchMap(_ => this.loadAddress(api)),
        )),
      )
    )
  }

  saveContacts(values: V3ContactEntry[]) {
    let observables: Observable<unknown>[] = new Array<Observable<unknown>>();
    this.savingObservables.push(
      this.apiProvider.casa().pipe(
        tap(api => {
          values.forEach(value => {
            value.id == null
              ? observables.push(api.createContact(this.personId, value))
              : observables.push(api.updateContact(this.personId, value.id, value))
          })
        }),
        switchMap(_ => concat(
          ...observables
        )),
        last(),
        switchMap(_ => this.apiProvider.casa()),
        switchMap(api => this.loadContacts(api)),
      ))
  }

  savePhoto(value: V3FileItem) {
    this.savingObservables.push(
      this.apiProvider.casa().pipe(
        switchMap(api => api.updatePhoto(this.personId, value).pipe(
          switchMap(_ => this.loadPhoto(api)),
        )),
      )
    )
  }

  confirmSaving() {
    this.busy = true
    concat(
      ...this.savingObservables
    ).pipe(
      finalize(() => {
        if (this.locale != this.settings.locale)
          location.reload()
        this.saved = true
        setTimeout(() => {
          this.saved = false
          this.busy = false
        }, 2000);
      })
    ).subscribe()
  }
}
